/**
 * Moves all child elements from a host element to a target element using a document fragment.
 * This is useful for efficiently transferring DOM nodes between elements while preserving their order.
 *
 * @param host - The element whose children will be moved
 * @param target - The element that will receive the children
 */
export function adoptLightChildren(
  host: HTMLElement,
  target: HTMLElement
): void {
  const frag = document.createDocumentFragment();
  while (host.firstChild) {
    frag.appendChild(host.firstChild);
  }
  target.appendChild(frag);
}

/**
 * Conditionally attaches children from a wrapper element to a target element.
 * If a wrapper is provided, all its children are moved to the target element.
 * If no wrapper is provided, no action is taken.
 *
 * @param element - The target element that will receive the children
 * @param wrapper - Optional wrapper element whose children will be moved. If undefined, no action is taken.
 */
export function attachChildren(
  element: HTMLElement,
  wrapper?: HTMLDivElement
): void {
  if (wrapper) adoptLightChildren(element, wrapper);
}

/**
 * Merges multiple ref callbacks into a single ref callback function.
 * This is useful when you need to apply multiple refs to the same element,
 * such as combining forwardRef with local refs.
 *
 * @template T - The type of element the refs will be applied to
 * @param refs - Variable number of ref callbacks. Each callback receives the element and can be undefined.
 * @returns A single ref callback that applies all provided refs to the element
 *
 * @example
 * ```tsx
 * const ref1 = useRef<HTMLDivElement>(null);
 * const ref2 = useRef<HTMLDivElement>(null);
 *
 * return (
 *   <div ref={mergeRefs(ref1, ref2, forwardRef)} />
 * );
 * ```
 */
export function mergeRefs<T extends Element>(
  ...refs: (((el: T) => void) | undefined)[]
): (el: T) => void {
  return (el: T): void => {
    refs.forEach((ref): void => ref?.(el));
  };
}

/**
 * Idempotently inject a tiny static stylesheet into document.head.
 * Safe to call multiple times (or from multiple component instances) — the
 * DOM id-guard ensures the style tag is inserted at most once per page.
 * Never removed; intended only for page-lifetime, module-scoped stylesheets.
 * No-ops in non-DOM environments (SSR).
 */
export function injectScopedStyles(id: string, css: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

/**
 * Idempotently inject a tiny static stylesheet into a host element's shadow
 * root. Same contract as {@link injectScopedStyles}, scoped to the given
 * shadow root rather than document.head.
 */
export function injectShadowStyles(
  host: Element,
  id: string,
  css: string
): void {
  const sr = host.shadowRoot;
  if (!sr || sr.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  sr.appendChild(style);
}

/** Maximum animation-frame poll attempts before giving up on shadow root availability. */
const SHADOW_STYLE_MAX_ATTEMPTS = 8;

/**
 * Injects shadow styles once the shadow root is ready, polling up to
 * `maxAttempts` animation frames to handle Stencil's async shadow root
 * initialization. Also re-injects one frame after the first successful
 * injection to survive Stencil's first re-render, which replaces shadow root
 * contents and would otherwise remove the injected style tag.
 *
 * Use this instead of calling {@link injectShadowStyles} directly whenever the
 * host is a Stencil web component whose shadow root may not yet exist at call
 * time (e.g. inside `onMount` / `customElements.whenDefined(...).then(...)`).
 */
export function injectShadowStylesWhenReady(
  host: HTMLElement,
  id: string,
  css: string,
  attempt = 0,
  maxAttempts = SHADOW_STYLE_MAX_ATTEMPTS
): void {
  if (!host.shadowRoot) {
    if (attempt < maxAttempts) {
      requestAnimationFrame(() =>
        injectShadowStylesWhenReady(host, id, css, attempt + 1, maxAttempts)
      );
    }
    return;
  }
  injectShadowStyles(host, id, css);
  if (attempt === 0) {
    requestAnimationFrame(() =>
      injectShadowStylesWhenReady(host, id, css, 1, maxAttempts)
    );
  }
}

/**
 * Idempotently inject a stylesheet into the rendering context that owns the
 * given element, without the caller needing to know whether that context is a
 * shadow root or the document.
 *
 * - When the element lives inside a shadow root (e.g. rendered by a web
 *   component), `getRootNode()` returns that `ShadowRoot` and the style is
 *   injected there — making it visible to all scoped-mode (no-shadow) children
 *   inside the same WC instance.
 * - When the element lives in the regular light DOM, `getRootNode()` returns
 *   the `Document` and the style falls back to `document.head`.
 *
 * This handles the case where `modus-wc-dropdown-menu` (Stencil scoped mode,
 * no shadow root of its own) is slotted inside `tc-modus-dropdown-menu` (which
 * does have a shadow root): the styles need to live inside the outer shadow
 * root, not in `document.head`, otherwise the shadow boundary blocks them.
 *
 * Caller is responsible for ensuring the element is attached to the DOM before
 * calling (e.g. inside `onMount` / `customElements.whenDefined(...).then(...)`).
 *
 * No-ops in non-DOM environments (SSR) or if the style tag already exists.
 */
export interface WaitForWebComponentHydratedOptions {
  /** CSS selector used to find the element within `root`. */
  selector: string;
  /** Custom element tag name to wait for before polling (optional). */
  tagName?: string;
  /** Invoked once the matched element has Stencil's `hydrated` class. */
  onHydrated(): void;
  /** Register a cleanup callback (e.g. Solid's `onCleanup`). */
  registerCleanup(cleanup: () => void): void;
}

/**
 * Poll until a Stencil web component within `root` has the `hydrated` class,
 * then invoke `onHydrated`. No-ops in non-DOM environments (SSR).
 */
export function waitForWebComponentHydrated(
  root: ParentNode,
  options: WaitForWebComponentHydratedOptions
): void {
  if (typeof document === 'undefined') return;

  let cancelled = false;
  let animationFrameId: number | undefined;

  const pollForHydrated = (): void => {
    if (cancelled) return;

    const element = root.querySelector<HTMLElement>(options.selector);
    if (element?.classList.contains('hydrated')) {
      options.onHydrated();
      return;
    }

    animationFrameId = requestAnimationFrame(pollForHydrated);
  };

  const startPolling = (): void => {
    animationFrameId = requestAnimationFrame(pollForHydrated);
  };

  options.registerCleanup(() => {
    cancelled = true;
    if (animationFrameId !== undefined) {
      cancelAnimationFrame(animationFrameId);
    }
  });

  if (options.tagName) {
    customElements.whenDefined(options.tagName).then(() => {
      if (cancelled) return;
      startPolling();
    });
  } else {
    startPolling();
  }
}

export function injectInRenderContext(
  element: Element,
  id: string,
  css: string
): void {
  if (typeof document === 'undefined') return;
  const root = element.getRootNode();
  const target = root instanceof ShadowRoot ? root : document.head;
  let style = target.querySelector(`#${id}`);
  if (style) {
    if (style.textContent !== css) {
      style.textContent = css;
    }
    return;
  }
  style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  target.appendChild(style);
}

export type ComposedOutsideClickElements =
  | Array<HTMLElement | null | undefined>
  | (() => Array<HTMLElement | null | undefined>);

/**
 * Returns true when the event target lies inside any of the given elements.
 * Uses composedPath() so clicks inside nested or slotted shadow DOM are detected.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 21 > 15 allowed; refactor later
export function isEventInsideElements(
  e: Event,
  elements: Array<HTMLElement | null | undefined>
): boolean {
  const path: EventTarget[] =
    typeof (e as Event & { composedPath?(): EventTarget[] }).composedPath ===
    'function'
      ? (e as Event & { composedPath(): EventTarget[] }).composedPath()
      : ((e as Event & { path?: EventTarget[] }).path ?? []);

  for (const el of elements) {
    if (!el) {
      continue;
    }
    if (path.length > 0 && path.includes(el)) {
      return true;
    }
    const shadowRoot = el.shadowRoot;
    if (shadowRoot && path.length > 0) {
      for (const node of path) {
        if (node instanceof Node && shadowRoot.contains(node)) {
          return true;
        }
      }
    }
    if (e.target instanceof Node && el.contains(e.target)) {
      return true;
    }
  }

  return false;
}

/**
 * Registers document-level listeners that invoke `onOutside` when a click/touch
 * occurs outside all provided elements. Returns a cleanup function.
 */
export function addComposedOutsideClickListener(
  elements: ComposedOutsideClickElements,
  onOutside: (e: Event) => void,
  events: string[] = ['mousedown', 'touchstart']
): () => void {
  const handler = (e: Event): void => {
    const targets = typeof elements === 'function' ? elements() : elements;
    if (isEventInsideElements(e, targets)) {
      return;
    }
    onOutside(e);
  };

  for (const ev of events) {
    document.addEventListener(ev, handler);
  }

  return () => {
    for (const ev of events) {
      document.removeEventListener(ev, handler);
    }
  };
}
